// The pop-up behind "Assign manager" on Dashboard > Deployment > Undeployed Stores: the
// Trade Heads and AMs the ticked stores lie under while nobody is deployed at them.
//
// Two lists (product owner, 28 Oct 2026): Trade Head first, then Area Manager (AM),
// several of each. The AM list holds only the AMs who report, directly or further down,
// to the Trade Heads chosen (every AM while none is), and taking a Trade Head off takes off
// the AMs no longer under any chosen one. Everyone chosen shows as a chip that can be
// taken off. A project that lacks either role (by role name) has one list of all its
// managers, so nobody who could be picked before drops out. The pop-up starts with the
// people the ticked stores already share. At most MAX_PEOPLE per store, as the server
// allows.
import { useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import Modal from '../Modal';
import { FormErrorBanner } from '../FormErrorBanner';
import MultiSearchableSelect from '../MultiSearchableSelect';
import type { SearchableOption } from '../SearchableSelect';
import { storeVacancyManagersAPI } from '../../services/api';
import { byKindThenName, readVacancyManagers } from './useStoreVacancyManagers';
import type {
  StoreVacancyManager, StoreVacancyManagersResponse, VacancyManagerOption, VacancyManagerOptions, VacancyRoleKind,
} from './storeVacancyManagers';

interface AssignStoreManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number | null;
  /** Every store id the ticked rows stand for. */
  storeIds: string[];
  /** The ticked rows, as the list counts them. */
  storeCount: number;
  /** At least one ticked store has a manager, so there are some to remove. */
  canRemove: boolean;
  /** The people every ticked store shares, to start with; 'mixed' when the stores differ. */
  current?: StoreVacancyManager[] | 'mixed';
  /** The save went through: the assignments it answered with, or null when they could not be read. */
  onSaved: (answer: StoreVacancyManagersResponse | null) => void;
}

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const keyOf = (code: string): string => code.trim().toLowerCase();

/** The pick lists out of the answer, read field by field. */
const readOptions = (response: unknown): VacancyManagerOptions => {
  const data = (response as { data?: { data?: { managers?: unknown; reportingKnown?: unknown } } } | undefined)?.data?.data;
  const list = Array.isArray(data?.managers) ? data.managers : [];
  const managers: VacancyManagerOption[] = [];
  for (const raw of list as Array<Partial<Record<keyof VacancyManagerOption, unknown>> | null>) {
    if (typeof raw?.code !== 'string' || !raw.code.trim()) continue;
    managers.push({
      code: raw.code,
      name: typeof raw.name === 'string' && raw.name.trim() ? raw.name : raw.code,
      roleName: typeof raw.roleName === 'string' && raw.roleName.trim() ? raw.roleName : null,
      kind: raw.kind === 'trade_head' || raw.kind === 'am' ? raw.kind : null,
      ...(Array.isArray(raw.tradeHeads) ? { tradeHeads: raw.tradeHeads.filter((c): c is string => typeof c === 'string') } : {}),
      ...(raw.teamKnown === false ? { teamKnown: false } : {}),
    });
  }
  return { managers, reportingKnown: data?.reportingKnown !== false };
};

/** The most people one store may have (the server's MAX_MANAGERS_PER_STORE). */
const MAX_PEOPLE = 25;

/**
 * Two lists only when the project has both a Trade Head and an AM among its managers;
 * otherwise one list of everyone, so a project whose AM (or Trade Head) role is named in
 * a way not read as one still offers every manager.
 */
const splitsInTwo = (managers: readonly VacancyManagerOption[]): boolean =>
  managers.some((m) => m.kind === 'trade_head') && managers.some((m) => m.kind === 'am');

/** Why there is one list: which of the two roles the project lacks. */
const oneListNote = (managers: readonly VacancyManagerOption[]): string => {
  const heads = managers.some((m) => m.kind === 'trade_head');
  const ams = managers.some((m) => m.kind === 'am');
  const missing = heads ? 'AM' : ams ? 'Trade Head' : 'Trade Head or AM';
  return `No ${missing} role in this project, so every manager is in one list.`;
};

interface Person { code: string; name: string; roleName: string | null }

/** "Name" (or "Name · Role" where the lists are not split); two alike are told apart by their codes. */
const choicesOf = (people: readonly Person[], withRole: boolean): SearchableOption[] => {
  const textOf = (p: Person) => (withRole ? [p.name || p.code, p.roleName].filter(Boolean).join(' · ') : p.name || p.code);
  const timesSeen = new Map<string, number>();
  for (const p of people) timesSeen.set(textOf(p), (timesSeen.get(textOf(p)) ?? 0) + 1);
  return people.map((p) => ({
    value: p.code,
    label: (timesSeen.get(textOf(p)) ?? 0) > 1 ? `${textOf(p)} · ${p.code}` : textOf(p),
  }));
};

interface Chosen {
  tradeHeads: string[];
  ams: string[];
  /** Anyone else: the one list of a project with neither role, or someone already on the stores who is in neither list. */
  others: string[];
}
const NOBODY: Chosen = { tradeHeads: [], ams: [], others: [] };

export default function AssignStoreManagerModal(props: AssignStoreManagerModalProps) {
  // Drawn only while open, so every opening starts clean: nobody picked, no old message.
  return props.isOpen && props.projectId ? <AssignStoreManager {...props} projectId={props.projectId} /> : null;
}

function AssignStoreManager({
  onClose, projectId, storeIds, storeCount, canRemove, current = [], onSaved,
}: AssignStoreManagerModalProps & { projectId: number }) {
  // null until the managers arrive.
  const [options, setOptions] = useState<VacancyManagerOptions | null>(null);
  const [chosen, setChosen] = useState<Chosen>(NOBODY);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  // Read once, as the pop-up opens: the people the ticked stores share now.
  const [startWith] = useState(() => (current === 'mixed' ? [] : [...current].sort(byKindThenName)));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const found = readOptions(await storeVacancyManagersAPI.managers(projectId));
        if (!alive) return;
        setOptions(found);
        // Each person the stores already have goes in the list they are in today (else the one they were chosen from).
        const kindNow = new Map(found.managers.map((m) => [keyOf(m.code), m.kind]));
        const split = splitsInTwo(found.managers);
        const start: Chosen = { tradeHeads: [], ams: [], others: [] };
        for (const m of startWith) {
          const kind: VacancyRoleKind | null = kindNow.has(keyOf(m.managerCode)) ? kindNow.get(keyOf(m.managerCode)) ?? null : m.managerKind;
          if (split && kind === 'trade_head') start.tradeHeads.push(m.managerCode);
          else if (split && kind === 'am') start.ams.push(m.managerCode);
          else start.others.push(m.managerCode);
        }
        setChosen(start);
      } catch (err) {
        if (!alive) return;
        setOptions({ managers: [], reportingKnown: true });
        setFailed(true);
        setError(apiMessage(err, 'Could not load the managers. Close this and try again.'));
      }
    })();
    return () => { alive = false; };
  }, [projectId, startWith]);

  const managers = useMemo(() => options?.managers ?? [], [options]);
  // Two lists, unless the project lacks a Trade Head or an AM (then one list of every
  // manager). Until the managers are read, and when they cannot be, the two lists.
  const split = options === null || failed || splitsInTwo(managers);
  const reportingKnown = options?.reportingKnown !== false;

  /** Everyone who can be named here: the pick list, and the people the stores already have. */
  const people = useMemo(() => {
    const out = new Map<string, Person>();
    for (const m of startWith) {
      out.set(keyOf(m.managerCode), {
        code: m.managerCode,
        name: m.managerActive ? m.managerName : `${m.managerName} (left)`,
        roleName: m.managerRole,
      });
    }
    for (const m of managers) out.set(keyOf(m.code), { code: m.code, name: m.name, roleName: m.roleName });
    return out;
  }, [managers, startWith]);
  const personOf = (code: string): Person => people.get(keyOf(code)) ?? { code, name: code, roleName: null };
  /** The list's own people, plus whoever is chosen in it, so nobody chosen ever drops out of sight. */
  const withChosen = (list: readonly Person[], codes: readonly string[]): Person[] => {
    const have = new Set(list.map((p) => keyOf(p.code)));
    return [...list, ...codes.filter((code) => !have.has(keyOf(code))).map(personOf)];
  };

  const tradeHeadPeople = useMemo(() => managers.filter((m) => m.kind === 'trade_head'), [managers]);
  const amPeople = useMemo(() => managers.filter((m) => m.kind === 'am'), [managers]);
  /** Trade Heads (lower-case codes) whose team could not be read: choosing one lists every AM. */
  const teamUnknown = useMemo(
    () => new Set(tradeHeadPeople.filter((th) => th.teamKnown === false).map((th) => keyOf(th.code))),
    [tradeHeadPeople],
  );
  /** The AMs a set of Trade Heads allows (as lower-case codes); null when every AM is allowed. */
  const amsAllowedUnder = (tradeHeads: readonly string[]): Set<string> | null => {
    if (tradeHeads.length === 0 || !reportingKnown) return null;
    // One whose team is not known could have anyone under them.
    if (tradeHeads.some((th) => teamUnknown.has(keyOf(th)))) return null;
    const chosenHeads = new Set(tradeHeads.map(keyOf));
    return new Set(amPeople.filter((am) => (am.tradeHeads ?? []).some((th) => chosenHeads.has(keyOf(th)))).map((am) => keyOf(am.code)));
  };
  const allowed = amsAllowedUnder(chosen.tradeHeads);
  const amList = allowed ? amPeople.filter((am) => allowed.has(keyOf(am.code))) : amPeople;

  const setTradeHeads = (next: string[]) => {
    const under = amsAllowedUnder(next);
    const ams = under ? chosen.ams.filter((code) => under.has(keyOf(code))) : chosen.ams;
    const dropped = chosen.ams.length - ams.length;
    setChosen({ ...chosen, tradeHeads: next, ams });
    setNote(dropped > 0 ? `Removed ${dropped} AM${dropped === 1 ? '' : 's'} not under the chosen Trade Heads.` : null);
  };
  const setAms = (next: string[]) => { setChosen({ ...chosen, ams: next }); setNote(null); };
  const setOthers = (next: string[]) => { setChosen({ ...chosen, others: next }); setNote(null); };

  const codes = [...chosen.tradeHeads, ...chosen.ams, ...chosen.others];
  const tooMany = codes.length - MAX_PEOPLE;
  const unknownChosen = chosen.tradeHeads.filter((th) => teamUnknown.has(keyOf(th)));
  const stores = `${storeCount} store${storeCount === 1 ? '' : 's'}`;
  const loading = options === null;

  /** Give the stores exactly these people, or clear them with []. */
  const save = async (managerCodes: string[]) => {
    setSaving(true);
    setError(null);
    try {
      onSaved(readVacancyManagers(await storeVacancyManagersAPI.assign(projectId, storeIds, managerCodes)));
    } catch (err) {
      setError(apiMessage(err, managerCodes.length > 0
        ? 'Could not assign the managers. Please try again.'
        : 'Could not remove the managers. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    if (window.confirm(`Remove the managers from ${stores}?`)) void save([]);
  };

  const chips = (list: readonly string[], onChange: (next: string[]) => void) => (
    list.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-1.5" role="group">
        {list.map((code) => {
          const { name } = personOf(code);
          return (
            <span key={code} className="flex max-w-full items-center gap-1 rounded-full bg-primary-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-primary-700">
              <span className="truncate">{name}</span>
              <button
                type="button"
                onClick={() => onChange(list.filter((c) => c !== code))}
                disabled={saving}
                aria-label={`Remove ${name}`}
                className="flex h-5 w-5 flex-none items-center justify-center rounded-full text-primary-600 hover:bg-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          );
        })}
      </div>
    )
  );

  const LABEL = 'mb-1 block text-sm font-medium text-gray-700';
  const HINT = 'mt-1.5 text-xs text-gray-500';

  return (
    <Modal
      isOpen
      // A save in flight is let finish: closing now would hide whether it went through.
      onClose={() => { if (!saving) onClose(); }}
      title="Assign manager"
      size="md"
      footer={(
        <>
          {canRemove && (
            <button
              type="button"
              onClick={remove}
              disabled={saving}
              className="mr-auto text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              Remove managers
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { void save(codes); }}
            disabled={codes.length === 0 || tooMany > 0 || saving || loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500">{stores}</p>
        {current === 'mixed' && (
          <p className="text-xs text-amber-700">These stores have different managers now. Save gives them all the people chosen here.</p>
        )}
        {error && <FormErrorBanner message={error} />}
        {split ? (
          <>
            <div>
              <label htmlFor="assign-trade-heads" className={LABEL} title="The store shows for each Trade Head chosen, and for everyone above them">
                Trade Head
              </label>
              <MultiSearchableSelect
                id="assign-trade-heads"
                values={chosen.tradeHeads}
                onChange={setTradeHeads}
                options={choicesOf(withChosen(tradeHeadPeople, chosen.tradeHeads), false)}
                placeholder={loading ? 'Loading managers…' : 'Choose Trade Heads…'}
                disabled={loading || saving}
                emptyText="No Trade Heads found"
              />
              {chips(chosen.tradeHeads, setTradeHeads)}
            </div>
            <div>
              <label
                htmlFor="assign-ams"
                className={LABEL}
                title="Only the AMs who report to the Trade Heads chosen, by the 1Office reporting lines; every AM while none is chosen. The store shows for each AM chosen, and for everyone above them."
              >
                Area Manager (AM)
              </label>
              <MultiSearchableSelect
                id="assign-ams"
                values={chosen.ams}
                onChange={setAms}
                options={choicesOf(withChosen(amList, chosen.ams), false)}
                placeholder={loading ? 'Loading managers…' : 'Choose AMs…'}
                disabled={loading || saving}
                emptyText={allowed ? 'No AM reports to these Trade Heads' : 'No AMs found'}
              />
              {chips(chosen.ams, setAms)}
              {note && <p className={HINT} role="status">{note}</p>}
              {!loading && !reportingKnown && (
                <p className={HINT}>Could not read who reports to whom, so every AM is listed.</p>
              )}
              {!loading && reportingKnown && unknownChosen.length > 0 && (
                <p className={HINT}>
                  {unknownChosen.length === 1
                    ? `Could not read who reports to ${personOf(unknownChosen[0]).name}, so every AM is listed.`
                    : `Could not read who reports to ${unknownChosen.length} of these Trade Heads, so every AM is listed.`}
                </p>
              )}
            </div>
            {chosen.others.length > 0 && (
              <div>
                <span className={LABEL}>Also on these stores</span>
                {chips(chosen.others, setOthers)}
              </div>
            )}
          </>
        ) : (
          <div>
            <label htmlFor="assign-managers" className={LABEL}>Managers</label>
            <MultiSearchableSelect
              id="assign-managers"
              values={chosen.others}
              onChange={setOthers}
              options={choicesOf(withChosen(managers, chosen.others), true)}
              placeholder="Choose managers…"
              disabled={saving}
              emptyText="No managers found"
            />
            {chips(chosen.others, setOthers)}
            <p className={HINT} title="Trade Head and AM are read from the names of the project's roles.">{oneListNote(managers)}</p>
          </div>
        )}
        {tooMany > 0 && (
          <p className="text-xs font-medium text-red-600" role="alert">
            {`At most ${MAX_PEOPLE} people per store. Remove ${tooMany}.`}
          </p>
        )}
      </div>
    </Modal>
  );
}
